import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCollection,
  getId,
  getPermissionLabel,
  requestJson,
  stripEmptyValues,
} from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useMenuPage } from '../hooks/useMenuPage';

const emptyForm = {
  code: '',
  label: '',
  permissionIds: [],
};

const defaultPermissions = [
  { code: 'VIEW_STOCK_LENSES', module: 'stock', label: 'Voir les verres de stock' },
  { code: 'MANAGE_STOCK_LENSES', module: 'stock', label: 'Gérer les verres de stock' },
  { code: 'VIEW_COMMANDES', module: 'commandes', label: 'Voir les commandes' },
  { code: 'MANAGE_COMMANDES', module: 'commandes', label: 'Gérer les commandes' },
  { code: 'VIEW_CLIENTS', module: 'clients', label: 'Voir les clients' },
  { code: 'MANAGE_CLIENTS', module: 'clients', label: 'Gérer les clients' },
  { code: 'VIEW_USERS', module: 'users', label: 'Voir les utilisateurs' },
  { code: 'MANAGE_USERS', module: 'users', label: 'Gérer les utilisateurs' },
  { code: 'VIEW_SITES', module: 'sites', label: 'Voir les sites' },
  { code: 'MANAGE_SITES', module: 'sites', label: 'Gérer les sites' },
  { code: 'VIEW_ROLES', module: 'roles', label: 'Voir les rôles' },
  { code: 'MANAGE_ROLES', module: 'roles', label: 'Gérer les rôles' },
  { code: 'VIEW_STATISTICS', module: 'statistics', label: 'Voir les statistiques' },
  { code: 'MANAGE_LOGISTICS_SUPPLIERS', module: 'logistics', label: 'Gérer les fournisseurs Logistique' },
];

export default function RolesPage() {
  const menuPage = useMenuPage('/roles');
  const [rows, setRows] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/roles');
      setRows(getCollection(payload));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    try {
      const payload = await requestJson('/permissions');
      setPermissions(getCollection(payload));
    } catch (_err) {
      setPermissions([]);
    }
  }, []);

  useEffect(() => {
    loadRows();
    loadPermissions();
  }, [loadRows, loadPermissions]);

  const seedPermissions = async () => {
    try {
      setError('');
      await Promise.all(
        defaultPermissions.map((permission) =>
          requestJson('/permissions', {
            method: 'POST',
            body: JSON.stringify(permission),
          }).catch(() => null)
        )
      );
      await loadPermissions();
    } catch (err) {
      setError(err.message);
    }
  };

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: row._id,
        permissionsLabel: (row.permissionIds || []).map(getPermissionLabel).join(', '),
        permissionCount: row.permissionIds?.length || 0,
      })),
    [rows]
  );

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenEdit = useCallback((row) => {
    setForm({
      code: row.code || '',
      label: row.label || '',
      permissionIds: (row.permissionIds || []).map(getId),
    });
    setEditingId(row._id);
    setOpen(true);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError('');
      await requestJson(editingId ? `/roles/${editingId}` : '/roles', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(stripEmptyValues(form)),
      });
      setOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        setError('');
        await requestJson(`/roles/${id}`, { method: 'DELETE' });
        setDeleteTarget(null);
        await loadRows();
      } catch (err) {
        setError(err.message);
      }
    },
    [loadRows]
  );

  const columns = useMemo(
    () => [
      { field: 'code', headerName: 'Code', minWidth: 180, flex: 0.8 },
      { field: 'label', headerName: 'Rôle', minWidth: 220, flex: 1 },
      {
        field: 'permissionCount',
        headerName: 'Permissions',
        minWidth: 140,
        renderCell: (params) => (
          <Chip size="small" label={params.value} color="primary" variant="outlined" />
        ),
      },
      { field: 'permissionsLabel', headerName: 'Droits', minWidth: 420, flex: 1.5 },
      {
        field: 'active',
        headerName: 'Actif',
        minWidth: 100,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value === false ? 'Non' : 'Oui'}
            color={params.value === false ? 'default' : 'success'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        minWidth: 120,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [handleOpenEdit]
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 850 }}>
          {menuPage.sectionTitle}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {menuPage.pageTitle}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Recherche rapide"
            value={quickFilter}
            onChange={(event) => {
              const value = event.target.value;
              setQuickFilter(value);
              setFilterModel((current) => ({ ...current, quickFilterValues: value ? [value] : [] }));
            }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={seedPermissions}>
              Permissions par défaut
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
              Rafraîchir
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Ajouter
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={gridRows}
              columns={columns}
              slots={{ toolbar: GridToolbar }}
              slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              filterModel={filterModel}
              onFilterModelChange={(newModel) => {
                setFilterModel(newModel);
                setQuickFilter(newModel.quickFilterValues?.[0] || '');
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{ border: 0 }}
            />
          </Box>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Modifier un rôle' : 'Ajouter un rôle'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField name="code" label="Code rôle" value={form.code} onChange={handleChange} required />
              <TextField name="label" label="Libellé" value={form.label} onChange={handleChange} required />
              <TextField
                name="permissionIds"
                label="Permissions"
                select
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    permissions
                      .filter((permission) => selected.includes(permission._id))
                      .map(getPermissionLabel)
                      .join(', '),
                }}
                value={form.permissionIds}
                onChange={handleChange}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission._id} value={permission._id}>
                    <Checkbox checked={form.permissionIds.includes(permission._id)} />
                    <ListItemText primary={getPermissionLabel(permission)} secondary={permission.module} />
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`le rôle ${deleteTarget?.code || deleteTarget?.label || ''}`}
        description={`Confirmer la suppression du rôle ${
          deleteTarget?.code || deleteTarget?.label || ''
        } ?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />
    </Stack>
  );
}
